import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import FloatingHeader from '../components/layout/FloatingHeader';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import { useInstaller } from '../components/InstallerDownload';
import { Fx, titelBild, titelPfad } from '../components/produktseite/Kopf';
import Lichtszene from '../components/licht/Lichtszene';
import '../components/produktseite/produktseite.css';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';
import KaufHinweis from '../components/kauf/KaufHinweis';
import AboZustimmung from '../components/kauf/AboZustimmung';

// Das Abo hat eine eigene Seite, weil es kein Produkt ist, sondern der Zugang zu allen: Studio Flow,
// die zehn Werkzeuge, Instant Grade, Instant Comp, Flow Panel und alles, was dazukommt. Studio Flow
// ist ein Produkt IM Abo, der Name des Abos ist All-in-One.
//
// Aufbau (Davids Wunsch 07.10.2026: auf einen Blick klar machen, was drin ist): Kopf mit Preis und Start,
// „Drei Panels. Alles drin.“ (große Studio-Flow-Box mit den zehn Werkzeugen darin, daneben + Instant Grade
// und + Instant Comp), „Immer aktuell“, Vergleich zum Einzelkauf, Installation, Fragen.

// Die zehn Werkzeuge, die zusammen in Studio Flow stecken
const IM_STUDIO = [
  ['instant-curve', 'Instant Curve'], ['bounce-flow', 'Bounce Flow'], ['shake-flow', 'Shake Flow'], ['glitch-flow', 'Glitch Flow'],
  ['text-flow', 'Text Flow'], ['grid-flow', 'Grid Flow'], ['carousel-flow', 'Carousel Flow'], ['shape-flow', 'Shape Flow'],
  ['background-flow', 'Background Flow'], ['instant-ui-kit', 'Instant UI Kit'],
];
const EINZELPREIS_MODUL = 14.99, EINZELPREIS_GROSS = 24.99;
// Titelbilder (ProduktBilder/out/titel-all-in-one.png und titel-studio-flow.png, 07.10.2026)
const BILD_STUDIO_FLOW = '/products/titel/studio-flow.jpg';
const ABO_JAHR = 159, ABO_MONAT = 15.99;

const Haken = ({ an }) => an
  ? <svg className="vh an" viewBox="0 0 24 24" aria-label="ja"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
  : <svg className="vh" viewBox="0 0 24 24" aria-label="nein"><path d="M7 7l10 10M17 7L7 17" /></svg>;

export default function AllInOne() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const en = i18n.language === 'en' || location.pathname.startsWith('/en');
  const p = en ? '/en' : '';
  const T = (de, enText) => (en ? enText : de);
  const [jaehrlich, setJaehrlich] = useState(true);
  // Zustimmungs-Dialog vor der Abo-Kasse (AGB/Widerruf + § 356 Abs. 6 Nr. 2 BGB, wie im Warenkorb)
  const [aboOffen, setAboOffen] = useState(false);
  const info = useInstaller();
  const wurzel = useRef(null);
  const buehne = useRef(null);

  const euro = (v, stellen = 2) => { const z = v.toFixed(stellen); return en ? '€' + z : z.replace('.', ',') + ' €'; };
  const einzelnSumme = IM_STUDIO.length * EINZELPREIS_MODUL + 2 * EINZELPREIS_GROSS;

  // Abo läuft nicht über den Warenkorb — Stripe mischt Abonnement und Einmalzahlung nicht in einer
  // Sitzung. Vor dem Sprung zu Stripe kommt die Zustimmung (AboZustimmung), die dann die Kasse öffnet.
  const starten = () => setAboOffen(true);

  // Einblenden beim Scrollen, Glas-Ränder mit Cursor-Licht, versetzter Light Sweep
  useEffect(() => {
    const el = wurzel.current;
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { threshold: .12 });
    el.querySelectorAll('.reveal, .reveal-text').forEach(x => io.observe(x));
    el.querySelectorAll('.btn.primaer').forEach(b => b.style.setProperty('--sv', (1 + Math.random() * 4).toFixed(2) + 's'));
    const bewegen = e => {
      const fx = e.target.closest && e.target.closest('.fx');
      if (fx && el.contains(fx)) {
        const r = fx.getBoundingClientRect();
        fx.style.setProperty('--mx', (e.clientX - r.left).toFixed(0) + 'px');
        fx.style.setProperty('--my', (e.clientY - r.top).toFixed(0) + 'px');
      }
    };
    window.addEventListener('pointermove', bewegen, { passive: true });
    return () => { io.disconnect(); window.removeEventListener('pointermove', bewegen); };
  }, []);

  // Studio-Flow-Ansichten; die Aufnahmen tragen noch den alten Namen, darum liegt die Titelleiste darüber
  // Kopf: der Mauszeiger klickt sich durch die Seitenleiste von Studio Flow. Die Aufnahmen sind echte
  // Studio-Flow-Screenshots (1600×900); x/y = Mitte des Symbols in der Seitenleiste in Prozent des Bildes.
  // Ihre Titelleiste trägt noch den Einzelnamen, darüber liegt fest „Studio Flow“.
  const STATIONEN = [
    ['instant-curve', 10.5, T('Kurven', 'Curves')],
    ['bounce-flow', 16.0, 'Bounce'],
    ['shake-flow', 21.9, 'Shake'],
    ['text-flow', 46.3, 'Text'],
    ['grid-flow', 53.6, T('Raster', 'Grid')],
    ['carousel-flow', 59.4, T('Karussell', 'Carousel')],
  ];

  useEffect(() => {
    const el = buehne.current;
    if (!el) return undefined;
    const bilder = [...el.querySelectorAll('.aio-bild')];
    const zeiger = el.querySelector('.aio-zeiger'), welle = el.querySelector('.aio-welle'), anzeige = el.querySelector('.aio-bereich');
    const timer = [];
    const spaeter = (fn, ms) => timer.push(setTimeout(fn, ms));
    const zeige = i => {
      bilder.forEach((b, k) => b.classList.toggle('an', k === i));
      if (anzeige) anzeige.textContent = STATIONEN[i][2];
    };
    zeige(0);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let i = 0;
    const schritt = () => {
      const n = (i + 1) % STATIONEN.length;
      // hinfahren
      zeiger.style.top = STATIONEN[n][1] + '%';
      spaeter(() => {
        // klicken
        zeiger.classList.add('drueckt');
        welle.style.top = STATIONEN[n][1] + '%';
        welle.classList.remove('los'); void welle.offsetWidth; welle.classList.add('los');
        spaeter(() => zeiger.classList.remove('drueckt'), 160);
        zeige(n); i = n;
        spaeter(schritt, 2300);
      }, 900);
    };
    zeiger.style.top = STATIONEN[0][1] + '%';
    spaeter(schritt, 1600);
    return () => timer.forEach(clearTimeout);
  }, [en]);

  const vergleich = [
    [T('Die zehn Einzel-Werkzeuge', 'The ten single tools'), true, true, T('je 14,99 €', '€14.99 each')],
    [T('Studio Flow: alles in einem Panel', 'Studio Flow: all in one panel'), true, false, T('zehn getrennte Panels', 'ten separate panels')],
    ['Instant Grade', true, true, euro(24.99)],
    ['Instant Comp', true, true, euro(24.99)],
    [T('Workflow-Werkzeuge', 'Workflow tools'), true, false, T('nicht käuflich', 'not for sale')],
    [T('Neue Werkzeuge', 'New tools'), true, false, T('jedes einzeln kaufen', 'buy each one')],
    ['Updates', true, false, T('nur Fehlerbehebungen', 'bug fixes only')],
  ];

  const faq = [
    [T('Was steckt im All-in-One?', 'What’s in the All-in-One?'),
     T('Studio Flow mit allen Werkzeugen in einem Panel, dazu Instant Grade, Instant Comp, das Flow Panel und jedes neue Werkzeug, sobald es fertig ist.',
       'Studio Flow with every tool in one panel, plus Instant Grade, Instant Comp, the Flow Panel and every new tool as soon as it’s ready.')],
    [T('Was kostet es?', 'What does it cost?'),
     T(`${euro(ABO_MONAT)} im Monat oder ${euro(ABO_JAHR, 0)} im Jahr, das sind zwei Monate geschenkt. In den 7 Tagen Test wird nichts abgebucht.`,
       `${euro(ABO_MONAT)} a month or ${euro(ABO_JAHR, 0)} a year, which gives you two months free. Nothing is charged during the 7-day trial.`)],
    [T('Kann ich jederzeit kündigen?', 'Can I cancel any time?'), T('Ja, das All-in-One ist jederzeit kündbar.', 'Yes, you can cancel the All-in-One any time.')],
    [T('Gibt es Studio Flow auch einzeln?', 'Is Studio Flow sold separately?'),
     T('Nein, Studio Flow gibt es nur im All-in-One. Einzeln kaufen kannst du die Werkzeuge, jedes in seinem eigenen Panel.', 'No, Studio Flow is only available in the All-in-One. You can buy the tools separately, each in its own panel.')],
    [T('Wie installiere ich alles?', 'How do I install everything?'),
     T('Mit dem kostenlosen David Media Installer: laden, mit deinem Konto anmelden, und er richtet alles ein, was im All-in-One steckt.', 'With the free David Media Installer: download it, sign in with your account, and it sets up everything in the All-in-One.')],
  ];

  const startKnopf = (extra = '') => (
    <button type="button" className={'btn primaer ' + extra} onClick={starten} aria-haspopup="dialog" data-testid={extra ? 'aio-start-unten' : 'aio-start'}>
      {T('7 Tage kostenlos testen', 'Start 7-day free trial')}
    </button>
  );

  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title="All-in-One | DAVIDMEDIA"
        description={en
          ? 'One subscription, every David Media tool for After Effects — Studio Flow, Instant Grade, Instant Comp and everything new.'
          : 'Ein Abo, alle David-Media-Werkzeuge für After Effects — Studio Flow, Instant Grade, Instant Comp und alles Neue.'}
        url={`https://www.davidmedia.de${p}/shop/all-in-one`}
        // Vorschaubild beim Teilen (ProduktBilder/out/og-all-in-one-1200.png); Text darauf ist deutsch
        image={en ? undefined : 'https://www.davidmedia.de/products/og/all-in-one.jpg'}
      />
      <FloatingHeader />
      {/* Rückkehr aus der Abo-Kasse (über /shop/abo-jahr bzw. /shop/abo-monat, siehe ProductDetail) */}
      <KaufHinweis art="abo" en={en} />
      {/* Zustimmung vor der Abo-Kasse; ein Wechsel Monatlich/Jährlich im Dialog stellt den Umschalter der Seite mit um */}
      <AboZustimmung offen={aboOffen} plan={jaehrlich ? 'jahr' : 'monat'} en={en} onZu={() => setAboOffen(false)} onPlan={w => setJaehrlich(w === 'jahr')} />
      <div className="produktseite" ref={wurzel}>
        <Lichtszene ohneStaub />
        {/* Kopf füllt den ersten Bildschirm wie Startseite, Installer und Support: links Text und Preis,
            rechts das schräge Studio-Flow-Panel, durch das sich der Mauszeiger klickt */}
        <section className="aio-held" data-testid="aio-hero" ref={buehne}>
          <div className="aio-buehne" aria-hidden="true">
            <div className="aio-halo" />
            <div className="aio-panel">
              {/* Instant Grade und Instant Comp als Karten hinter Studio Flow gestapelt: nur ihre Titelleisten schauen oben raus */}
              <div className="aio-stapel s2"><img src="/produkt/vorlagen/instant-grade.webp" alt="" /><div className="ptitel">Instant Grade</div></div>
              <div className="aio-stapel s1"><img src="/produkt/vorlagen/instant-comp.webp" alt="" /><div className="ptitel">Instant Comp</div></div>
              <div className="aio-schirm">
                {STATIONEN.map(([id]) => (
                  <div className="aio-bild" key={id}>
                    <img src={`/startseite/panel/${id}.webp`} alt="" />
                    <img className="unscharf" src={`/startseite/panel/${id}.webp`} alt="" />
                  </div>
                ))}
                <div className="aio-glanz" />
                <div className="ptitel">Studio Flow</div>
                <span className="aio-welle" />
                <svg className="aio-zeiger" viewBox="0 0 24 24"><path d="M5 2.5l14 10.6-6.2 1.1-3.6 6.3z" fill="#fff" stroke="#111" strokeWidth="1.3" strokeLinejoin="round" /></svg>
              </div>
              <span className="aio-marke">Studio <em>Flow</em> · <b className="aio-bereich" /></span>
            </div>
            {/* schwebende Keyframe-Rauten wie auf der Startseite */}
            <i className="aio-raute r1" /><i className="aio-raute r2" /><i className="aio-raute r4" />
          </div>
          <Lichtszene vorne />
          <div className="aio-text" data-testid="aio-kasse">
            <div className="brotkrumen"><Link to={`${p}/shop`}>{T('Werkzeuge', 'Tools')}</Link> / <span>All-in-One</span></div>
            <span className="kicker">{T('Das Abo · alles dabei', 'Subscription · everything included')}</span>
            <h1>{T('Alle Werkzeuge.', 'Every tool.')} <span className="grau">{T('Ein Abo.', 'One subscription.')}</span></h1>
            <div className="aio-punkte">
              <div><b><span>Studio Flow</span></b><small>{T('alle Werkzeuge in einem Panel', 'every tool in one panel')}</small></div>
              <div><b><span>+ 2</span> {T('Panels', 'panels')}</b><small>{T('Instant Grade und Instant Comp', 'Instant Grade and Instant Comp')}</small></div>
              <div><b><span>{T('Jedes', 'Every')}</span> {T('Update', 'update')}</b><small>{T('und jedes neue Werkzeug automatisch', 'and every new tool automatically')}</small></div>
            </div>
            <div className="aio-preis">
              <div className="umschalter">
                {[{ j: false, l: T('Monatlich', 'Monthly') }, { j: true, l: T('Jährlich', 'Yearly') }].map(w => (
                  <button key={w.l} className={jaehrlich === w.j ? 'an' : ''} onClick={() => setJaehrlich(w.j)} data-testid={`aio-wahl-${w.j ? 'jahr' : 'monat'}`}>{w.l}</button>
                ))}
              </div>
              <div className="preiszeile">
                <span className="preis">{jaehrlich ? euro(ABO_JAHR, 0) : euro(ABO_MONAT)}</span>
                <small>{jaehrlich
                  ? T(`/ Jahr · nur ${euro(ABO_JAHR / 12)} im Monat`, `/ year · only ${euro(ABO_JAHR / 12)} a month`)
                  : T('/ Monat · monatlich kündbar', '/ month · cancel any month')}</small>
              </div>
            </div>
            <div className="ctas">
              {startKnopf()}
              <Link className="btn glas" to={`${p}/shop`}>{T('Lieber einzeln', 'Buy separately')}</Link>
            </div>
            <p className="notiz">{T('In den 7 Tagen wird nichts abgebucht. Jederzeit kündbar.', 'No charge during the 7-day trial. Cancel any time.')}</p>
          </div>
        </section>

        <section className="sek" id="enthalten" data-testid="aio-enthalten" style={{ paddingTop: 50 }}>
          <div className="innen">
            <div className="kopf reveal-text">
              <span className="kicker">{T('Das ist alles drin', 'What’s included')}</span>
              <h2><span className="zl"><span>{T('Drei Panels.', 'Three panels.')}</span></span><span className="zl"><span className="grau">{T('Alles drin.', 'Everything inside.')}</span></span></h2>
            </div>
            <div className="drin">
              <div className="studio-box fx reveal" data-testid="aio-kachel-studio-flow">
                <div className="studio-text">
                  <div className="studio-oben">
                  <Link className="studio-bild" to={`${p}/shop/studio-flow`}><img {...titelBild(titelPfad(BILD_STUDIO_FLOW, en), false, '(max-width: 1100px) 100vw, 40vw')} alt="Studio Flow" loading="lazy" /></Link>
                  <div className="studio-kopf">
                    <div>
                      <span className="marke-klein">{T('Nur im All-in-One', 'Only in All-in-One')}</span>
                      <h3>Studio Flow</h3>
                      <p>{T('Alle Werkzeuge zusammen in einem einzigen Panel: die zehn Einzel-Werkzeuge, dazu Transition Flow und die Workflow-Werkzeuge, die es nur hier gibt.',
                        'Every tool together in one single panel: the ten single tools, plus Transition Flow and the workflow tools you only get here.')}</p>
                    </div>
                    <Link className="btn glas" to={`${p}/shop/studio-flow`}>{T('Studio Flow ansehen', 'See Studio Flow')} <span aria-hidden="true">→</span></Link>
                  </div>
                  </div>
                  <span className="mini-titel">{T('Darin stecken diese zehn Werkzeuge:', 'These ten tools are inside:')}</span>
                  <div className="mini-raster">
                    {IM_STUDIO.map(([id, name]) => (
                      <Link key={id} className="mini" to={`${p}/shop/${id}`} data-testid={`aio-kachel-${id}`}>
                        <img {...titelBild(titelPfad(`/products/titel/${id}.jpg`, en), false, '(max-width: 760px) 50vw, 240px')} alt="" loading="lazy" /><span>{name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
                <Fx />
              </div>
              {[['instant-grade', 'Instant Grade', T('Color-Grading direkt in After Effects, mit eigenen Looks und LUTs.', 'Color grading right in After Effects, with your own looks and LUTs.')],
                ['instant-comp', 'Instant Comp', T('Eigene Comps als Bausteine sichern und per Klick einsetzen.', 'Save your own comps as building blocks and insert them in one click.')]].map(([id, name, text], i) => (
                <Link key={id} className="extra-box fx reveal" to={`${p}/shop/${id}`} style={{ transitionDelay: `${(i + 1) * 80}ms` }} data-testid={`aio-kachel-${id}`}>
                  <div className="extra-bild"><img {...titelBild(titelPfad(`/products/titel/${id}.jpg`, en), false, '(max-width: 1100px) 100vw, 30vw')} alt={name} loading="lazy" /></div>
                  <div className="extra-text">
                    <div className="zeile"><b><span className="plus" aria-hidden="true">+</span>{name}</b><span>{T('eigenes Panel', 'own panel')}</span></div>
                    <p>{text}</p>
                  </div>
                  <Fx />
                </Link>
              ))}
            </div>
            <p className="gratis-zeile reveal">{T('Dazu gratis für alle:', 'Free for everyone on top:')} <Link to={`${p}/shop/flow-panel`}>{T('das Flow Panel', 'the Flow Panel')} →</Link></p>
          </div>
        </section>

        <section className="sek" id="aktuell" style={{ paddingTop: 20 }}>
          <div className="innen">
            <div className="kopf reveal-text">
              <span className="kicker">{T('Immer aktuell', 'Always up to date')}</span>
              <h2><span className="zl"><span>{T('Einmal abonniert.', 'Subscribe once.')}</span></span><span className="zl"><span className="grau">{T('Es wird immer mehr.', 'It keeps growing.')}</span></span></h2>
            </div>
            <div className="vorteile">
              {[
                [T('Jedes Update automatisch', 'Every update automatically'), T('Neue Versionen kommen über den Installer, ein Klick auf „Alle aktualisieren“ genügt.', 'New versions arrive through the installer, one click on “Update all” is enough.')],
                [T('Jedes neue Werkzeug sofort dabei', 'Every new tool included right away'), T('Was neu fertig wird, landet ohne Aufpreis in deinem Abo und in Studio Flow.', 'Whatever is newly finished lands in your subscription and in Studio Flow at no extra cost.')],
                [T('Einzeln: nur Fehlerbehebungen', 'Separately: bug fixes only'), T('Wer einzeln kauft, bekommt Fehlerbehebungen für sein Werkzeug. Neue Funktionen und Werkzeuge gibt es im All-in-One.', 'Single purchases get bug fixes for their tool. New features and tools come with the All-in-One.')],
              ].map(([titel, text], i) => (
                <div className="vorteil fx reveal" key={titel} style={{ transitionDelay: `${i * 70}ms` }}>
                  <span className="vnr">0{i + 1}</span><h3>{titel}</h3><p>{text}</p>
                  <Fx />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sek" id="vergleich" data-testid="abo-vergleich">
          <div className="innen">
            <div className="kopf reveal-text">
              <span className="kicker">{T('Vergleich', 'Comparison')}</span>
              <h2><span className="zl"><span>{T('All-in-One oder einzeln.', 'All-in-One or separately.')}</span></span><span className="zl"><span className="grau">{T('Der Unterschied auf einen Blick.', 'The difference at a glance.')}</span></span></h2>
            </div>
            <div className="vergleich reveal">
              <div className="vz kopfzeile"><span /><b>All-in-One</b><span>{T('Einzeln gekauft', 'Bought separately')}</span></div>
              {vergleich.map(([was, imAbo, einzeln, zusatz]) => (
                <div className="vz" key={was}>
                  <span>{was}</span>
                  <span><Haken an={imAbo} /></span>
                  <span><Haken an={einzeln} /> {zusatz && <small>{zusatz}</small>}</span>
                </div>
              ))}
              <div className="vz summe">
                <span>{T('Preis', 'Price')}</span>
                <b>{euro(ABO_JAHR, 0)}<small>{T('/ Jahr', '/ year')}</small></b>
                <span>{T(`${euro(einzelnSumme)} einmalig`, `${euro(einzelnSumme)} once`)}</span>
              </div>
            </div>
            <p className="mikro reveal" style={{ marginTop: 16 }}>{T('Alles einzeln zu kaufen kostet mehr als das erste Abo-Jahr und bleibt auf dem heutigen Stand.', 'Buying everything separately costs more than the first year and stays at today’s state.')}</p>
            <div className="ctas reveal" style={{ marginTop: 24 }}>
              {startKnopf('unten')}
              <Link className="btn glas" to={`${p}/shop/studio-flow`}>{T('Studio Flow ansehen', 'See Studio Flow')}</Link>
            </div>
          </div>
        </section>

        <section className="sek" id="installer-sek">
          <div className="innen inst">
            <div className="inst-bild reveal"><img src="/produkt/installer.webp" alt="" loading="lazy" /></div>
            <div className="reveal-text">
              <span className="kicker">{T('Installation', 'Installation')}</span>
              <h2><span className="zl"><span>{T('In einer Minute drin.', 'Up and running in a minute.')}</span></span><span className="zl"><span className="grau">{T('Und immer aktuell.', 'And always up to date.')}</span></span></h2>
              <p className="lead reveal">{T('Der kostenlose David Media Installer richtet alles ein, was im All‑in‑One steckt, und hält es aktuell.', 'The free David Media Installer sets up everything in the All‑in‑One and keeps it up to date.')}</p>
              <ol className="schritte reveal">
                <li><span><b>{T('Installer laden', 'Download the installer')}</b>{T(' für Mac oder Windows.', ' for Mac or Windows.')}</span></li>
                <li><span><b>{T('Mit deinem Konto anmelden.', 'Sign in with your account.')}</b>{T(' Das Konto ist kostenlos.', ' The account is free.')}</span></li>
                <li><span><b>{T('In After Effects öffnen:', 'Open it in After Effects:')}</b>{T(' Fenster › Erweiterungen.', ' Window › Extensions.')}</span></li>
              </ol>
              <div className="ctas reveal">
                <a className="btn primaer" href={info ? info.url : undefined}>{T('Für Mac laden', 'Download for Mac')}</a>
                <a className="btn glas" href={info ? info.url_win : undefined}>{T('Für Windows laden', 'Download for Windows')}</a>
              </div>
            </div>
          </div>
        </section>

        <section className="sek" style={{ paddingTop: 20 }}>
          <div className="innen">
            <span className="kicker">{T('Fragen', 'Questions')}</span>
            <h2>{T('Gut zu wissen.', 'Good to know.')}</h2>
            <div className="faq">
              {faq.map(([f, a], i) => <details key={f} open={i === 0}><summary>{f}</summary><p>{a}</p></details>)}
            </div>
          </div>
        </section>
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </motion.div>
  );
}
